import { createMiddleware } from 'redux-beacon';
import debounceEvent from '@redux-beacon/debounce-event';
import SegmentEventType from 'enums/segmentEventType';
import { Segment } from 'middlewares/segment';
import { addMiddleware } from 'redux-dynamic-middlewares';
import getSegmentEvent from 'services/segmentEventBuilder';
import { segmentIdentify, segmentImpression, segmentPageView, segmentTrack } from './actions';

/**
 * Dispatches a Segment analytics event with optional debouncing.
 * 
 * Supported event types are the `SegmentEventType` values used by this middleware:
 * `Track`, `Identify`, `Impression`, and `PageView`.
 * 
 * Track, Identify, and Impression payloads may include an `eventName`.
 * PageView payloads are also supported and may omit `eventName`.
 * 
 * @param {string} eventType - The Segment event type to dispatch
 * (for example, `SegmentEventType.Track` or `SegmentEventType.PageView`).
 * @param {Object} payload - The event payload containing event data.
 * @param {boolean} [payload.skipDebounce] - If true, bypasses the debounce mechanism and dispatches immediately.
 * @param {string} payload.eventName - The event name for Track, Identify, or Impression events;
 * not required for PageView events.
 * @param {Object} [payload.customProperties] - Additional custom properties to include with the event.
 * @returns {Function|Object} Returns the result of `getSegmentEvent` directly if `skipDebounce` is true, 
 *                            otherwise returns a debounced version with a 1000ms delay.
 */
const dispatchEvent = (eventType, payload) => {
  const { skipDebounce, customProperties, eventName } = payload;
  const segmentEvent = getSegmentEvent(eventType, { eventName, customProperties });
  if (skipDebounce) {
    return segmentEvent;
  }
  return debounceEvent(1000, segmentEvent);
};

/**
 * The eventsMap function is a mapping of Redux actions to Segment events.
 * It returns an array of Segment events, which are objects with the following shape:
 * {
 *   eventType: string, // The name of the event (e.g., 'track')
 *   fieldsObj: object, // An object containing all properties for this event type (e.g., {eventName})
 * }
 *
 * @param action Determine which event to return
 *
 * @return An array of events
 */
const eventsMap = (action) => {
  switch (action.type) {
    case segmentTrack.type: {
      const payload = action.payload;
      const event = dispatchEvent(SegmentEventType.Track, payload);
      return event;
    }
    case segmentIdentify.type: {
      const payload = action.payload;
      const event = dispatchEvent(SegmentEventType.Identify, payload);
      return event;
    }

    case segmentImpression.type: {
      const payload = action.payload;
      const event = dispatchEvent(SegmentEventType.Impression, payload);
      return event;
    }

    case segmentPageView.type: {
      const payload = action.payload;
      const event = debounceEvent(
        1000,
        getSegmentEvent(SegmentEventType.PageView, {
          category: payload.category,
          name: payload.name,
          customProperties: payload.customProperties,
          options: payload.options,
        })
      );
      return event;
    }

    default:
      return [];
  }
};

/* `const segment = Segment();` is creating an instance of the `@redux-beacon/segment` library, which
is used to send events to the Segment analytics platform. This instance is then passed as an
argument to the `createMiddleware` function to create a middleware that listens for Redux actions
and sends corresponding Segment events using the `segment` instance. */
const segment = new Segment();

/* `const middleware = createMiddleware(eventsMap, segment);` is creating a middleware using
`redux-beacon` library. The `createMiddleware` function takes two arguments: `eventsMap` and
`segment`. `eventsMap` is a function that maps Redux actions to Segment events, and `segment` is an
instance of the `@redux-beacon/segment` library. The middleware created by `createMiddleware` will
listen for Redux actions and send corresponding Segment events using the `segment` instance. */
const middleware = createMiddleware(eventsMap, segment.getTarget());

// registers the middleware in redux
/* `addMiddleware(middleware)` is registering the middleware created by `createMiddleware` in the Redux
store. This allows the middleware to listen for Redux actions and send corresponding Segment events
using the `segment` instance. */
addMiddleware(middleware);
